import {
    CalendarOutlined,
    CheckOutlined,
    CloseOutlined,
} from '@ant-design/icons';
import { Card, DatePicker, Input, Switch, Typography } from 'antd';
import AuthorSelector from 'components/AuthorSelector';
import FormField from 'components/FormField';
import GuideTypeSelector from 'components/GuideTypeSelector';
import LocationSelector from 'components/LocationSelector';
import { MediaSelector } from 'components/MediaSelector';
import PublishStatusSelector from 'components/PublishStatusSelector';
import {
    GUIDE_CUSTOM_LOCATION_MAX_LENGTH,
    GUIDE_DESCRIPTION_MAX_LENGTH,
    GUIDE_TAGLINE_MAX_LENGTH,
} from 'constants/fields-limits';
import { GUIDE_COVER_RATIO } from 'constants/media-sizes.constants';
import { format, parseISO } from 'date-fns';
import dayjs from 'dayjs';
import React, { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    patchGuideField,
    patchGuideLocation,
} from 'store/guide-editor/actions';
import { getCurrentGuide } from 'store/guide-editor/selectors';
import GuideRankingSelector from '../GuideRankingSelector';
import GuideQuickFacts from './GuideQuickFacts';

const { TextArea } = Input;
const { Text } = Typography;

function GuideEditorBasicInfo() {
    const dispatch = useDispatch();
    const guide = useSelector(getCurrentGuide);

    const isOrientationContentOnly = useMemo(() => {
        if (!guide?.id) {
            return true;
        }
        return guide.orientation_content_only ?? false;
    }, [guide?.id, guide?.orientation_content_only]);

    const publishedAt = useMemo(() => {
        if (!guide?.published_at) {
            return '';
        }
        return format(parseISO(guide.published_at), 'd MMM y HH:mm');
    }, [guide?.published_at]);

    if (!guide) {
        return null;
    }

    return (
        <>
            <div className="guide-editor-basic-info__container guide-editor-read-only-content">
                <div>
                    <Card title="Basic info">
                        <FormField label="Guide type">
                            <GuideTypeSelector
                                value={guide.guide_type.id}
                                onChange={(newId, newGuideType) => {
                                    dispatch(
                                        patchGuideField(
                                            'guide_type',
                                            newGuideType,
                                        ),
                                    );
                                }}
                            />
                        </FormField>

                        <FormField label="Location">
                            <LocationSelector
                                value={guide.location}
                                onChange={(newLocation) => {
                                    dispatch(patchGuideLocation(newLocation));
                                }}
                            />
                        </FormField>
                        {guide.location &&
                            !(guide.viewport_area || guide.search_area) && (
                                <div className="mb-l">
                                    There is no viewport selected for this
                                    guide, please reselect the location for it
                                    to be automatically updated.
                                </div>
                            )}
                        <FormField
                            label="Cust. location name"
                            length={guide.location_name_custom.length}
                            maxLength={GUIDE_CUSTOM_LOCATION_MAX_LENGTH}
                        >
                            <Input
                                value={guide.location_name_custom}
                                readOnly
                                onChange={(e) =>
                                    dispatch(
                                        patchGuideField(
                                            'location_name_custom',
                                            e.target.value,
                                        ),
                                    )
                                }
                                size="large"
                                placeholder="Optional"
                            />
                        </FormField>

                        <FormField
                            label="Tagline"
                            length={guide.tagline.length}
                            maxLength={GUIDE_TAGLINE_MAX_LENGTH}
                        >
                            <Input
                                value={guide.tagline}
                                readOnly
                                onChange={(e) =>
                                    dispatch(
                                        patchGuideField(
                                            'tagline',
                                            e.target.value,
                                        ),
                                    )
                                }
                                size="large"
                                placeholder=""
                            />
                        </FormField>

                        <FormField
                            label="Description"
                            length={(guide.description || '').length}
                            maxLength={GUIDE_DESCRIPTION_MAX_LENGTH}
                        >
                            <TextArea
                                className="form-field__textarea"
                                autoSize={{ minRows: 1, maxRows: 6 }}
                                value={guide.description || ''}
                                readOnly
                                onChange={(e) =>
                                    dispatch(
                                        patchGuideField(
                                            'description',
                                            e.target.value,
                                        ),
                                    )
                                }
                                size="large"
                                placeholder="Optional"
                            />
                        </FormField>

                        <FormField label="Map visibility">
                            <GuideRankingSelector
                                value={guide.guide_ranking.id}
                                onChange={(newId, newGuideRanking) => {
                                    dispatch(
                                        patchGuideField(
                                            'guide_ranking',
                                            newGuideRanking,
                                        ),
                                    );
                                }}
                            />
                        </FormField>

                        <FormField label="Status">
                            <PublishStatusSelector
                                value={guide.publish_status?.id}
                                onChange={(newId, newValue) => {
                                    dispatch(
                                        patchGuideField(
                                            'publish_status',
                                            newValue,
                                        ),
                                    );
                                }}
                            />
                        </FormField>

                        <FormField label="Editor">
                            <AuthorSelector
                                value={guide.main_editor_id}
                                onChange={(newEditorId, newEditor) => {
                                    dispatch(
                                        patchGuideField(
                                            'main_editor_id',
                                            newEditorId,
                                        ),
                                    );
                                    dispatch(
                                        patchGuideField(
                                            'main_editor',
                                            newEditor,
                                        ),
                                    );
                                }}
                            />
                        </FormField>
                        <FormField label="Orientation content only">
                            <Switch
                                checked={isOrientationContentOnly}
                                checkedChildren={<CheckOutlined />}
                                unCheckedChildren={<CloseOutlined />}
                                onChange={(checked) =>
                                    dispatch(
                                        patchGuideField(
                                            'orientation_content_only',
                                            checked,
                                        ),
                                    )
                                }
                                defaultChecked={false}
                            />
                        </FormField>
                    </Card>
                    <div className="mb-m" />
                    <Card title="Meta info">
                        <FormField
                            label="Description"
                            length={guide.meta_description?.length}
                            maxLength={160}
                        >
                            <TextArea
                                value={guide.meta_description}
                                readOnly
                                onChange={(e) =>
                                    dispatch(
                                        patchGuideField(
                                            'meta_description',
                                            e.target.value,
                                        ),
                                    )
                                }
                                size="large"
                                placeholder="Optional"
                            />
                        </FormField>
                    </Card>
                    <div className="mb-m" />
                    <Card title="Quick Look">
                        <GuideQuickFacts
                            value={guide.quick_facts}
                            onChange={(newQuickFacts) =>
                                dispatch(
                                    patchGuideField(
                                        'quick_facts',
                                        newQuickFacts,
                                    ),
                                )
                            }
                        />
                    </Card>
                </div>
                <div>
                    <Card title="Cover photo" bodyStyle={{ padding: 0 }}>
                        <MediaSelector
                            location={guide.location}
                            spot={null}
                            guideId={guide.id}
                            includeVideos={false}
                            onSelect={(media) =>
                                dispatch(patchGuideField('cover_image', media))
                            }
                            onDelete={() =>
                                dispatch(patchGuideField('cover_image', null))
                            }
                            media={guide.cover_image}
                            ratio={GUIDE_COVER_RATIO}
                        />
                    </Card>
                    <div className="mb-m" />
                    <Card title="First publish date">
                        <Input
                            defaultValue={publishedAt}
                            readOnly
                            suffix={<CalendarOutlined />}
                        />
                        <div className="mt-sm" />
                        <Text type="secondary">
                            This field is completed automatically when you
                            publish the guide for the first time. New guides
                            appear on the “New Guides” section on guides
                            discovery in the app.
                        </Text>
                    </Card>
                    <div className="mb-m" />
                    <Card title="Last reviewed">
                        <FormField>
                            {/* @ts-ignore*/}
                            <DatePicker
                                defaultValue={
                                    guide.reviewed_at &&
                                    dayjs(guide.reviewed_at, 'YYYY-MM-DD')
                                }
                                format={'DD MMM YYYY'}
                                className="form-field__datepicker"
                                onChange={(e) =>
                                    dispatch(
                                        patchGuideField(
                                            'reviewed_at',
                                            e && e.format('YYYY-MM-DD'),
                                        ),
                                    )
                                }
                            />
                        </FormField>
                        <div className="mt-sm" />
                        <Text type="secondary">
                            Changing the date in this field affects the date
                            this guide was last reviewed.
                        </Text>
                    </Card>
                    <div className="mb-m" />
                    <Card title="Editor's notes">
                        <TextArea
                            className="form-field__textarea"
                            autoSize={{ minRows: 1, maxRows: 20 }}
                            value={guide.editor_notes || ''}
                            readOnly
                            onChange={(e) =>
                                dispatch(
                                    patchGuideField(
                                        'editor_notes',
                                        e.target.value,
                                    ),
                                )
                            }
                            size="large"
                            placeholder="Share your thoughts"
                        />
                    </Card>
                </div>
            </div>
        </>
    );
}

export default GuideEditorBasicInfo;
